import { supabase } from './supabase';

export interface Permissions {
  calendar: {
    pageAccess: boolean;
    sales?: boolean;
    viewAllBookings?: boolean;
    bookingInformation?: boolean;
    bookingSalesInfo?: boolean;
    editBookingDetails?: boolean;
    bookingMaterials?: boolean;
    bookingRatebook?: boolean;
    bookingProfitLoss?: boolean;
  };
  messages: {
    pageAccess: boolean;
  };
  crm: {
    pageAccess: boolean;
  };
  payroll: {
    pageAccess: boolean;
  };
  timeclock: {
    pageAccess: boolean;
  };
  supplies: {
    pageAccess: boolean;
  };
  services: {
    pageAccess: boolean;
  };
  resources: {
    pageAccess: boolean;
  };
  salesReports: {
    pageAccess: boolean;
  };
  teamReports: {
    pageAccess: boolean;
  };
  suppliesUsage: {
    pageAccess: boolean;
  };
  settings: {
    pageAccess: boolean;
  };
  tasks: {
    pageAccess: boolean;
  };
  bookings: {
    pageAccess: boolean;
  };
  proposals: {
    pageAccess: boolean;
  };
  customers: {
    pageAccess: boolean;
  };
  kpiDashboard: {
    pageAccess: boolean;
  };
  automations: {
    pageAccess: boolean;
  };
}

export interface ResourceRole {
  id: string;
  business_id: string;
  name: string;
  description: string;
  permissions: Permissions;
  created_at: string;
  updated_at: string;
}

export interface CreateRolePayload {
  name: string;
  description: string;
  business_id: string;
  permissions?: Partial<Permissions>;
}

// Get all roles for a business
export async function getResourceRoles(businessId: string): Promise<ResourceRole[]> {
  const { data, error } = await supabase
    .from('resource_roles')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Create a new role
export async function createResourceRole(payload: CreateRolePayload): Promise<ResourceRole> {
  // Default permissions structure (all false)
  const defaultPermissions: Permissions = {
    calendar: {
      pageAccess: false,
      sales: true,
      viewAllBookings: true,
      bookingInformation: true,
      bookingSalesInfo: true,
      editBookingDetails: true,
      bookingMaterials: true,
      bookingRatebook: true,
      bookingProfitLoss: true,
    },
    messages: {
      pageAccess: true,
    },
    crm: {
      pageAccess: true,
    },
    payroll: {
      pageAccess: true,
    },
    timeclock: {
      pageAccess: false,
    },
    supplies: {
      pageAccess: false,
    },
    services: {
      pageAccess: false,
    },
    resources: {
      pageAccess: false,
    },
    salesReports: {
      pageAccess: false,
    },
    teamReports: {
      pageAccess: false,
    },
    suppliesUsage: {
      pageAccess: false,
    },
    settings: {
      pageAccess: false,
    },
    tasks: {
      pageAccess: false,
    },
    bookings: {
      pageAccess: false,
    },
    proposals: {
      pageAccess: false,
    },
    customers: {
      pageAccess: false,
    },
    kpiDashboard: {
      pageAccess: false,
    },
    automations: {
      pageAccess: false,
    }
  };

  const { data, error } = await supabase
    .from('resource_roles')
    .insert([{
      business_id: payload.business_id,
      name: payload.name,
      description: payload.description,
      // Always provide permissions - merge with defaults if provided
      permissions: { ...defaultPermissions, ...payload.permissions }
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update a role
export async function updateResourceRole(
  roleId: string, 
  updates: Partial<Pick<ResourceRole, 'name' | 'description' | 'permissions'>>
): Promise<ResourceRole> {
  const { data, error } = await supabase
    .from('resource_roles')
    .update(updates)
    .eq('id', roleId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Delete a role
export async function deleteResourceRole(roleId: string): Promise<void> {
  const { error } = await supabase
    .from('resource_roles')
    .delete()
    .eq('id', roleId);

  if (error) throw error;
}

// Get role by ID
export async function getResourceRole(roleId: string): Promise<ResourceRole> {
  const { data, error } = await supabase
    .from('resource_roles')
    .select('*')
    .eq('id', roleId)
    .single();

  if (error) throw error;
  return data;
}

// Assign role to resource
export async function assignRoleToResource(resourceId: string, roleId: string | null): Promise<void> {
  const { error } = await supabase
    .from('resources')
    .update({ role_id: roleId })
    .eq('id', resourceId);

  if (error) throw error;
}

// Get resources with their assigned roles
export async function getResourcesWithRoles(businessId: string) {
  const { data, error } = await supabase
    .from('resources')
    .select(`
      *,
      role:resource_roles(*)
    `)
    .eq('business_id', businessId);

  if (error) throw error;
  return data;
} 